import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { F, PLAN_COLORS, withAlpha, mr, EASE, DUR } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { useCurrency } from '../context/CurrencyContext';
import { usePrivacy } from '../context/PrivacyContext';
import { useUserPlan } from '../context/UserPlanContext';
import { useProfile } from '../context/ProfileContext';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n/useT';
import { Icon, Icons } from '../components/red/Icons';
import { RedWordmark, RedSymbol, planLabel as planLabelOf } from '../components/red/Brand';
import SectionHeader from '../components/red/SectionHeader';
import SelectSheet from '../components/red/SelectSheet';
import { SettingSwitch, OptionPair } from '../components/red/QuickSettingsMenu';
import PromoCard from '../components/red/PromoCard';
import RedButton from '../components/red/RedButton';
import MemberCard, { MCGridMark } from '../components/red/MemberCard';
import { getOrAssignMemberNumber, formatMemberNo } from '../services/memberNumber';
import TierBadge from '../components/red/TierBadge';
import ExchangeLogo from '../components/ExchangeLogo';
import NotificationBell from '../components/NotificationBell';
import { API_EXCHANGES } from '../utils/exchanges';
import { buildDataCheck } from '../utils/dataCheck';
import { listConnections } from '../services/exchangeApi';
import { exportUserData, downloadUserDataAsJson, deleteAccountCompletely } from '../services/accountService';

const RED_PRO = '#B33E3A'; // ruhiges Mattrot (Calming-Pass) — speist Fills + Akzente

// ── Desktop Profile — nach source/red-screens-more.jsx Profile() ─────────────
// SectionHead "06 · Konto · Profil & Einstellungen."
// Hero (Avatar 64×64 mit Initial + Name + Mail + Plan-Pill + Upgrade + Logout)
// rw-main split:
//   LEFT: Verbundene Quellen, Einstellungen, Sicherheit, Daten & DSGVO
//   RIGHT (340px): Nutzung, Beta-Programm, Gefahrenzone
function DesktopProfile({
  T, t,
  isDark, setIsDark,
  currency, setCurrency,
  language, setLanguage,
  plan,
  name, initials,
  editingName, setEditingName,
  nameInput, setNameInput, saveName,
  user,
  connections, reloadConnections, setConnSheet,
  sources = [],
  txCount,
  positionCount,
  memberNo,
  isOwner,
  signOut,
  exporting, handleExportData,
  setConfirmReset,
  setConfirmDeleteAccount,
  onOpenPrivacy,
  onOpenPricing,
  onOpenApi,
  onOpenWallets,
  onOpenAlerts,
  onOpenSupport,
  setVipModal,
  modals,
}) {
  const initial = (name?.trim()?.[0] || initials?.[0] || '?').toUpperCase();
  const { privateMode, setPrivateMode } = usePrivacy();
  const [langSheet, setLangSheet] = useState(false);
  const [curSheet, setCurSheet] = useState(false);

  const tier = plan === 'vip' ? 'vip' : plan === 'premium' ? 'premium' : null;
  const lang = (language || 'de').toLowerCase();

  const memberSinceFull = (() => {
    try {
      const _ts = user?.metadata?.creationTime;
      if (!_ts) return '——';
      const d = new Date(_ts);
      const mo = d.toLocaleDateString('de-DE', { month: 'short' }).toUpperCase().replace('.', '');
      return `${mo}. ${d.getFullYear()}`;
    } catch { return '——'; }
  })();

  const Divider = () => <div style={{ height: 1, background: T.lineSoft || T.line }} />;
  // Ruhige Sentence-Case-Sektionsüberschrift — kein Koral-Punkt, kein Caps/Tracking
  // mehr (die alte „● ERSCHEINUNGSBILD"-Mono-Caps-Behandlung wurde entfernt).
  const SecHead = ({ label }) => (
    <div style={{ padding: '0 28px 10px' }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: T.sub, letterSpacing: '-0.01em', fontFamily: F.family }}>{label}</span>
    </div>
  );

  return (
    <div style={{ color: T.text, fontFamily: F.family, paddingBottom: 32 }}>

      {/* ── IDENTITY ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '28px 28px 24px' }}>
        <Avatar T={T} isDark={isDark} name={name} initials={initials} />
        <div style={{ flex: 1, minWidth: 0 }}>
          {editingName ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveName()}
                onBlur={saveName}
                autoFocus
                style={{ flex: 1, padding: '6px 10px', borderRadius: mr(12), background: T.surfaceHi, border: `1px solid ${T.line}`, color: T.text, fontSize: 18, fontFamily: F.display, fontWeight: 800 }}
              />
            </div>
          ) : (
            <div onClick={() => { setNameInput(name); setEditingName(true); }} style={{ cursor: 'pointer' }}>
              <div style={{ fontFamily: F.display, fontSize: 26, fontWeight: 900, letterSpacing: '-0.05em', lineHeight: 1.05, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {name || t('enter_name')}
              </div>
            </div>
          )}
          {!editingName && <TierBadge tier={tier} />}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <button
            onClick={() => { setNameInput(name); setEditingName(true); }}
            style={{ width: 36, height: 36, borderRadius: '50%', background: T.surface, border: `1px solid ${T.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            aria-label={t('prof_edit_name')}
          >
            <Icon d={Icons.edit} size={16} stroke={T.sub} sw={1.8} />
          </button>
          {plan !== 'vip' && (
            <button onClick={() => onOpenPricing?.()} style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px',
              background: 'transparent', color: T.text, border: `1px solid ${T.line}`, borderRadius: 999,
              fontFamily: F.family, fontSize: 13, fontWeight: 600, cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}>{t('upgrade')}</button>
          )}
        </div>
      </div>

      <Divider />

      {/* ── STATS ── */}
      <div style={{ display: 'flex', padding: '24px 28px' }}>
        {[
          { val: positionCount, lbl: t('positions') },
          { val: txCount,       lbl: t('transactions') },
          { val: sources.length, lbl: t('sources') },
        ].map(({ val, lbl }, i) => (
          <div key={lbl} style={{ flex: 1, ...(i > 0 ? { paddingLeft: 24, borderLeft: `1px solid ${T.lineSoft || T.line}` } : {}) }}>
            <div style={{ fontFamily: F.num, fontSize: 34, fontWeight: 900, letterSpacing: '-0.05em', lineHeight: 1 }}>{val}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.mute, letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: 6 }}>{lbl}</div>
          </div>
        ))}
      </div>

      <Divider />

      {/* ── MITGLIED NR. ── */}
      <div style={{ padding: '32px 28px 28px', position: 'relative', overflow: 'hidden' }}>
        <MCGridMark
          size={200}
          cells={withAlpha(T.text, 0.055)}
          dot={withAlpha(T.text, 0.055)}
          plus={T.red}
          style={{ position: 'absolute', right: -24, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
        />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.mute, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 8 }}>
            {t('member_since') || 'Mitglied seit'} {memberSinceFull}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.mute, letterSpacing: '0.04em', position: 'relative', top: -6 }}>Nº</span>
            <span style={{ fontFamily: F.num, fontSize: 80, fontWeight: 900, letterSpacing: '-0.06em', lineHeight: 0.9 }}>{memberNo}</span>
          </div>
        </div>
      </div>

      <Divider />

      {/* ── QUICK ACTION DOCK ── */}
      <div style={{ display: 'flex', padding: '20px 28px' }}>
        {[
          { icon: Icons.link,          label: t('sources') || 'Quellen',     action: () => onOpenApi?.() },
          { icon: Icons.walletCard,    label: t('wallets') || 'Wallets',     action: () => onOpenWallets?.() },
          { icon: Icons.bell,          label: t('alerts') || 'Alarme',       action: () => onOpenAlerts?.() },
          { icon: Icons.privacyShield, label: t('privacy') || 'Datenschutz', action: () => onOpenPrivacy?.() },
        ].map(({ icon, label, action }) => (
          <button
            key={label}
            onClick={action}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', WebkitTapHighlightColor: 'transparent' }}
          >
            <Icon d={icon} size={28} stroke={T.sub} sw={1.6} />
            <span style={{ fontSize: 12, fontWeight: 600, color: T.mute, letterSpacing: '-0.01em', fontFamily: F.family }}>{label}</span>
          </button>
        ))}
      </div>

      <Divider />

      {/* ── EINSTELLUNGEN + KONTO + DATEN — 2-Spalten-Grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, paddingTop: 24 }}>

        {/* Linke Spalte: Erscheinungsbild */}
        <div style={{ borderRight: `1px solid ${T.lineSoft || T.line}` }}>
          <SecHead label={t('appearance') || 'Einstellungen'} />
          <SettingRow icon={Icons.themeMoon} label={t('dark_mode')}    right={<Toggle on={isDark} onChange={setIsDark} />}           last={false} />
          <SettingRow icon={Icons.eyeOff}    label={t('private_mode')} right={<Toggle on={privateMode} onChange={setPrivateMode} />} last={false} />
          <SettingRow icon={Icons.language}  label={t('language')}     value={(language || 'de').toUpperCase()} onClick={() => setLangSheet(true)} last={false} />
          <SettingRow icon={Icons.currency}  label={t('currency')}     value={currency === 'USD' ? 'USD $' : 'EUR €'} onClick={() => setCurSheet(true)} last />
        </div>

        {/* Rechte Spalte: Konto + Daten */}
        <div>
          <SecHead label={t('account_security') || 'Konto'} />
          <SettingRow icon={Icons.headset}      label={t('support')}         onClick={() => onOpenSupport?.()}        last={false} />
          {isOwner && (
            <SettingRow icon={Icons.starFilled} label={t('prof_create_vip_code')} onClick={() => setVipModal(true)}   last={false} />
          )}
          <SettingRow icon={Icons.starFilled}   label={t('redeem_vip_code')} onClick={() => setVipModal(true)}        last={false} />
          <div style={{ height: 1, background: T.lineSoft || T.line, margin: '8px 0' }} />
          <SettingRow icon={Icons.export}        label={exporting ? t('exporting') : t('export_data_json')} onClick={exporting ? undefined : handleExportData} last={false} />
          <SettingRow icon={Icons.trashSettings} label={t('delete_data')} onClick={() => setConfirmReset(true)} last />
        </div>
      </div>

      {/* ── BETA PROGRAMM ── */}
      <style>{`@keyframes redCoralFx{0%{transform:translate(-5%,-4%) scale(1) rotate(0deg)}50%{transform:translate(5%,4%) scale(1.12) rotate(5deg)}100%{transform:translate(-3%,6%) scale(1.05) rotate(-4deg)}}@media (prefers-reduced-motion: reduce){.red-coralfx{animation:none!important}}`}</style>
      <div style={{ margin: '28px 16px 0', position: 'relative', overflow: 'hidden', background: 'linear-gradient(158deg, #23212A 0%, #17161B 56%, #100F13 100%)', borderRadius: 20, padding: '22px 22px 22px', display: 'flex', flexDirection: 'column', color: '#fff', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)' }}>
        <div className="red-coralfx" style={{ position: 'absolute', right: -56, top: '50%', width: 300, height: 300, marginTop: -150, filter: 'blur(10px)', pointerEvents: 'none', background: 'radial-gradient(circle at 50% 50%, rgba(228,84,74,.50), rgba(228,84,74,.14) 42%, transparent 68%)', animation: 'redCoralFx 13s ease-in-out infinite alternate' }} />
        <div aria-hidden="true" style={{ position: 'absolute', right: -42, top: '50%', transform: 'translateY(-50%) rotate(-8deg)', filter: 'drop-shadow(0 22px 34px rgba(0,0,0,0.46))', pointerEvents: 'none' }}>
          <MemberCard tier="vip" memberSince={String(new Date().getFullYear())} no="0001" width={190} />
        </div>
        <div style={{ position: 'relative', width: '60%', minWidth: 200 }}>
          <div style={{ paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.14)' }}>
            <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 9 }}>
              <span style={{ display: 'inline-flex', alignItems: 'baseline', lineHeight: 1, fontFamily: F.brand, fontWeight: 700, fontSize: 17, letterSpacing: '-0.03em', textTransform: 'lowercase', color: '#fff' }}>
                {t('prof_beta')}<span style={{ display: 'inline-block', width: 3, height: 3, borderRadius: '30%', background: '#E4544A', marginLeft: 1.5 }} />
              </span>
              <span style={{ fontFamily: F.brand, fontWeight: 500, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.46)' }}>{t('prof_program_suffix')}</span>
            </div>
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.32, color: '#fff' }}>
            {t('prof_beta_headline_pre')}<b style={{ color: '#E4544A', whiteSpace: 'nowrap' }}>{t('prof_beta_headline_accent')}</b>{t('prof_beta_headline_post')}
          </div>
          <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.66)', marginTop: 8, lineHeight: 1.5 }}>{t('prof_beta_sub')}</div>
          <button
            className="mo-row"
            onClick={() => { window.location.href = `mailto:support@redcrypto.de?subject=${encodeURIComponent(t('feedback_mail_subject'))}&body=${encodeURIComponent(t('feedback_mail_body'))}`; }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 16, padding: '11px 17px', background: 'linear-gradient(180deg, #E4544A 0%, #CE4138 100%)', color: '#fff', border: 'none', borderRadius: 12, boxShadow: '0 10px 24px rgba(206,65,56,0.38), inset 0 1px 0 rgba(255,255,255,0.22)', fontFamily: F.family, fontSize: 13, fontWeight: 700, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
          >
            <svg className="mo-lead" viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="#fff" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.4 8.4 0 0 1-9 8.4L3 21l1.1-3.8A8.4 8.4 0 1 1 21 11.5z"/></svg>
            {t('feedback_give')}
          </button>
        </div>
      </div>

      {/* ── ABMELDEN (iOS-Style) ── */}
      <div style={{ margin: '28px 16px 0', border: `0.5px solid ${T.line}`, borderRadius: 14, overflow: 'hidden', background: T.surface }}>
        <button
          onClick={async () => { try { await signOut(); } catch {} }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: 17, background: 'none', border: 'none', cursor: 'pointer', fontFamily: F.family, fontSize: 16, fontWeight: 400, color: T.red, WebkitTapHighlightColor: 'transparent' }}
        >
          {t('sign_out')}
        </button>
      </div>
      <div style={{ margin: '12px 16px 0', border: `0.5px solid ${T.line}`, borderRadius: 14, overflow: 'hidden', background: T.surface }}>
        <button
          onClick={() => setConfirmDeleteAccount(true)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: 14, background: 'none', border: 'none', cursor: 'pointer', fontFamily: F.family, fontSize: 14, fontWeight: 400, color: withAlpha(T.red, 0.45), WebkitTapHighlightColor: 'transparent' }}
        >
          {t('delete_account_full')}
        </button>
      </div>

      {/* Brand-Footer */}
      <div style={{ textAlign: 'center', padding: '32px 0 0' }}>
        <div style={{ display: 'inline-flex', justifyContent: 'center' }}>
          <RedWordmark size={22} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 32, marginTop: 20 }}>
          {[
            { icon: Icons.socialX,        href: 'https://x.com/redcrypto_de',                                    label: 'X',         filled: true  },
            { icon: Icons.socialInstagram, href: 'https://www.instagram.com/red_crypto_/',                       label: 'Instagram', filled: false },
            { icon: Icons.socialLinkedin,  href: 'https://www.linkedin.com/in/christian-rothlehner-7000863a0/', label: 'LinkedIn',  filled: false },
          ].map(({ icon, href, label, filled }) => (
            <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
              style={{ display: 'inline-flex', WebkitTapHighlightColor: 'transparent', color: T.red }}>
              <Icon d={icon} size={20} stroke={filled ? 'none' : T.red} sw={1.7} fill={filled ? T.red : 'none'} />
            </a>
          ))}
        </div>
        <div style={{ marginTop: 16, fontSize: 11, color: T.mute, lineHeight: 1.7 }}>
          <a href="https://redcrypto.de/impressum" target="_blank" rel="noopener noreferrer" style={{ color: T.mute, textDecoration: 'underline' }}>Impressum</a>
          {' · '}
          <a href="https://redcrypto.de/agb" target="_blank" rel="noopener noreferrer" style={{ color: T.mute, textDecoration: 'underline' }}>AGB</a>
          {' · '}
          <a href="https://redcrypto.de/datenschutz" target="_blank" rel="noopener noreferrer" style={{ color: T.mute, textDecoration: 'underline' }}>Datenschutz</a>
        </div>
      </div>

      {/* SelectSheets für Sprache + Währung */}
      <SelectSheet
        open={langSheet}
        title={t('language')}
        value={language || 'de'}
        onSelect={setLanguage}
        onClose={() => setLangSheet(false)}
        options={[
          { value: 'de', label: 'Deutsch (DE)' },
          { value: 'en', label: 'English (EN)' },
        ]}
      />
      <SelectSheet
        open={curSheet}
        title={t('currency')}
        value={currency}
        onSelect={setCurrency}
        onClose={() => setCurSheet(false)}
        options={[
          { value: 'EUR', label: 'EUR €' },
          { value: 'USD', label: 'USD $' },
        ]}
      />

      {/* Modals/Sheets durchgereicht */}
      {modals}
    </div>
  );
}

// Moderne Grafik für die „Meine Wallets"-Kachel: abstrakte Wallet-/Netzwerk-Illustration
// (eigene SVG, line-art, ein roter Akzent-Knoten = Chain). Kein Logo-Cluster, kein Glow.
function WalletArt({ T }) {
  const red = T.red || '#E4544A';
  return (
    <span style={{
      width: 52, height: 52, flexShrink: 0, borderRadius: 14,
      background: T.surfaceHi || T.surface, border: `1px solid ${T.line}`,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <svg width="30" height="30" viewBox="0 0 32 32" fill="none">
        {/* Verbindungen zu den Chain-Knoten */}
        <path d="M21 11 L26 7 M22 16 L28 16 M21 21 L26 25" stroke={T.sub} strokeWidth="1.4" strokeLinecap="round" opacity="0.7" />
        {/* Chain-Knoten */}
        <circle cx="27.5" cy="6" r="2.6" fill={red} />
        <circle cx="29" cy="16" r="2" fill={T.sub} />
        <circle cx="27.5" cy="26" r="2" fill={T.sub} />
        {/* Wallet-Karte */}
        <rect x="4" y="8" width="17" height="16" rx="3.5" stroke={T.text} strokeWidth="2" fill={T.surface} />
        {/* Verschluss-Knopf */}
        <rect x="14.5" y="14" width="6.5" height="4" rx="1.5" fill={T.text} />
      </svg>
    </span>
  );
}

// Designte „Meine Wallets"-Kachel — Grafik + Titel/Subtitel + Chevron.
// Press-Squish + Hover-Lift, unabhängig von prefers-reduced-motion (bewusst).
function WalletsCard({ T, t, onClick }) {
  const [hover, setHover] = useState(false);
  const [down, setDown] = useState(false);
  return (
    <button
      onClick={onClick}
      onPointerEnter={() => setHover(true)}
      onPointerDown={() => setDown(true)}
      onPointerUp={() => setDown(false)}
      onPointerLeave={() => { setDown(false); setHover(false); }}
      onPointerCancel={() => setDown(false)}
      style={{
        width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: F.family,
        border: `1px solid ${hover ? T.text : T.line}`, background: hover ? (T.itemHover || T.surfaceHi || T.surface) : T.surface,
        borderRadius: mr(16), padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 15,
        boxShadow: `${T.cardInset}, ${T.cardShadow}`, WebkitTapHighlightColor: 'transparent',
        transform: down ? 'scale(0.99)' : hover ? 'scale(1.008)' : 'scale(1)',
        // Theme-Wechsel-Flackern wird global unterdrueckt (ThemeContext +
        // index.css `html[data-theme-switching]`), darum hier wieder voller Hover.
        transition: `transform ${DUR.fast}ms cubic-bezier(0.16,1,0.3,1), border-color ${DUR.fast}ms ease, background ${DUR.fast}ms ease`,
      }}
    >
      <WalletArt T={T} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: T.text, letterSpacing: '-0.02em' }}>{t('wr_title')}</div>
        <div style={{ fontSize: 12, color: T.sub, marginTop: 3, lineHeight: 1.45, fontWeight: 500 }}>{t('wr_card_sub')}</div>
      </div>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: hover ? 'translateX(2px)' : 'translateX(0)', transition: `transform ${DUR.fast}ms ease` }}><path d="M9 18l6-6-6-6" /></svg>
    </button>
  );
}

// Buttons im Handoff-Style (Btn variants from red-ui.jsx)
function PrimaryBtn({ T, icon, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 9,
      padding: '9px 15px',
      background: 'linear-gradient(180deg, #C2453F 0%, #B33E3A 100%)', color: '#fff',
      border: '1px solid rgba(0,0,0,0.06)',
      borderRadius: mr(16),
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14), 0 1px 2px rgba(0,0,0,0.16)',
      fontFamily: F.family, fontWeight: 700, fontSize: 12.5,
      letterSpacing: '-0.01em', cursor: 'pointer',
    }}>
      {icon && <Icon d={Icons[icon] || Icons.bolt} size={15} stroke="#fff" sw={2.4} />}
      {children}
    </button>
  );
}

function GhostBtn({ T, icon, onClick, children }) {
  const [h, setH] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 9,
        padding: '9px 15px',
        background: h ? T.surfaceHi : 'transparent',
        color: T.text,
        border: `1.5px solid ${h ? withAlpha(T.text, 0.28) : T.line}`,
        borderRadius: mr(16),
        fontFamily: F.family, fontWeight: 700, fontSize: 12.5,
        letterSpacing: '-0.01em', cursor: 'pointer',
        transition: 'background .15s, border-color .15s',
      }}
    >
      {icon && <Icon d={Icons[icon] || Icons.back} size={15} stroke={T.text} sw={2.4} />}
      {children}
    </button>
  );
}

// Bundles existing modals/sheets so DesktopProfile can pass-through them.
// Component bodies copied to keep dependencies inside the desktop tree.
function DesktopProfileModals(props) {
  // Render existing modals via portal-like pattern: import existing sheet components
  // We re-use the same sheets that mobile uses by rendering them outside the layout.
  const { t } = useT();
  const {
    vipModal, setVipModal, vipCode, setVipCode, vipShake, handleVip,
    vipError, isOwner, creating, createdCode, handleCreateCode,
    confirmReset, setConfirmReset, onResetData,
    confirmDeleteAccount, setConfirmDeleteAccount, accountDeleting, accountDeleteError, handleDeleteAccount,
    T,
  } = props;
  return (
    <>
      {vipModal && createPortal((
        <div
          onClick={() => setVipModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: `calc(env(safe-area-inset-top, 0px) + 20px) 20px calc(env(safe-area-inset-bottom, 0px) + 20px)` }}
        >
          <div onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 360, padding: 24, borderRadius: mr(16),
              background: T.surface, border: `1px solid ${T.line}`,
              boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
              animation: vipShake ? 'vipShake 0.5s' : 'none',
            }}>
            <div style={{ fontFamily: F.display, fontSize: 18, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 8, color: T.text }}>{t('redeem_vip_code')}</div>
            <div style={{ fontSize: 12, color: T.sub, marginBottom: 14 }}>{t('prof_vip_code_desc')}</div>
            <input value={vipCode} onChange={e => setVipCode(e.target.value.toUpperCase())} onKeyDown={e => e.key === 'Enter' && handleVip()} placeholder="RED-VIP-…"
              autoComplete="off" autoCorrect="off" autoCapitalize="characters" spellCheck={false} enterKeyHint="go"
              style={{ width: '100%', padding: '12px 14px', borderRadius: mr(16), background: T.surfaceHi, border: `1px solid ${T.line}`, color: T.text, fontFamily: F.mono, fontSize: 16, letterSpacing: '0.06em', textAlign: 'center', boxSizing: 'border-box', outline: 'none' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <GhostBtn T={T} onClick={() => setVipModal(false)}>{t('cancel')}</GhostBtn>
              <PrimaryBtn T={T} onClick={handleVip}>{t('redeem')}</PrimaryBtn>
            </div>
            {vipError && (
              <div style={{ marginTop: 12, fontSize: 12, color: RED_PRO, textAlign: 'center' }}>{vipError}</div>
            )}
            {isOwner && (
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.line}` }}>
                <div style={{ fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.mute, marginBottom: 8 }}>{t('prof_owner_gift_code')}</div>
                {createdCode ? (
                  <>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div style={{ flex: 1, fontFamily: F.mono, fontSize: 14, letterSpacing: '0.06em', color: T.text, textAlign: 'center', padding: '10px 8px', background: T.surfaceHi || T.surface, borderRadius: mr(16), border: `1px solid ${T.line}` }}>{createdCode}</div>
                      <GhostBtn T={T} onClick={() => navigator.clipboard?.writeText(createdCode)}>{t('copy')}</GhostBtn>
                    </div>
                    <div style={{ marginTop: 8, fontSize: 11, color: T.mute, textAlign: 'center' }}>{t('prof_vip_once_redeemable')}</div>
                  </>
                ) : (
                  <GhostBtn T={T} onClick={handleCreateCode}>{creating ? t('creating') : t('prof_create_vip_code')}</GhostBtn>
                )}
              </div>
            )}
          </div>
        </div>
      ), document.body)}
      {confirmReset && (
        <div onClick={() => setConfirmReset(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 360, padding: 24, borderRadius: mr(16), background: T.surface, border: `1px solid ${T.line}`, color: T.text }}>
            <div style={{ fontFamily: F.display, fontSize: 18, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 8 }}>{t('delete_all_data_q')}</div>
            <div style={{ fontSize: 13, color: T.sub, marginBottom: 16 }}>{t('delete_confirm')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <GhostBtn T={T} onClick={() => setConfirmReset(false)}>{t('cancel')}</GhostBtn>
              <button onClick={async () => { try { await onResetData?.(); } catch {} finally { setConfirmReset(false); } }}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '9px 15px', background: RED_PRO, color: '#fff', border: 'none', borderRadius: mr(16), fontFamily: F.family, fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
                {t('delete')}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDeleteAccount && (
        <div onClick={() => setConfirmDeleteAccount(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 380, padding: 24, borderRadius: mr(16), background: T.surface, border: `1px solid ${T.line}`, color: T.text }}>
            <div style={{ fontFamily: F.display, fontSize: 18, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 8 }}>{t('delete_account_q')}</div>
            <div style={{ fontSize: 13, color: T.sub, marginBottom: 8 }}>{t('prof_delete_account_short_pre')}<strong>{t('irreversible')}</strong>{t('prof_delete_account_short_post')}</div>
            {accountDeleteError && (
              <div style={{ marginTop: 4, padding: '8px 10px', background: withAlpha(RED_PRO, 0.10), border: `1px solid ${withAlpha(RED_PRO, 0.30)}`, borderRadius: mr(16), fontSize: 12, color: RED_PRO }}>{accountDeleteError}</div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <GhostBtn T={T} onClick={() => setConfirmDeleteAccount(false)}>{t('cancel')}</GhostBtn>
              <button disabled={accountDeleting} onClick={handleDeleteAccount}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '9px 15px', background: RED_PRO, color: '#fff', border: 'none', borderRadius: mr(16), fontFamily: F.family, fontWeight: 700, fontSize: 12.5, cursor: accountDeleting ? 'default' : 'pointer', opacity: accountDeleting ? 0.6 : 1 }}>
                {accountDeleting ? t('prof_deleting') : t('prof_delete_account_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Toggle({ on, onChange }) {
  return (
    <div
      onClick={() => onChange(!on)}
      className="red-toggle"
      style={{
        width: 42,
        height: 25,
        borderRadius: 999,
        background: on ? '#E4544A' : 'rgba(150,150,160,0.3)',
        position: 'relative',
        cursor: 'pointer',
        transition: `background ${DUR.slow}ms ${EASE}`,
        WebkitTapHighlightColor: 'transparent',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 19 : 2,
          width: 21,
          height: 21,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.28)',
          transition: `left ${DUR.slow}ms ${EASE}`,
        }}
      />
    </div>
  );
}

// Profil-Avatar (Mockup brand/member-card-hero.html): aus dem MemberCard-Logo
// abgeleitet — squircle mit dem 2×2-Markenmotiv (eine Kachel koralle) als faintem
// Rahmen + großem Monogramm (1 ODER 2 Initialen). App-theme-aware (hell im Tagmodus,
// dunkel in der Nacht) — gehört sichtbar zur MemberCard.
function Avatar({ T, isDark, name, initials, size = 64 }) {
  const monogram = (() => {
    const src = (name || '').trim();
    if (src) {
      const p = src.split(/\s+/).filter(Boolean);
      if (p.length >= 2) return (p[0][0] + p[p.length - 1][0]).toUpperCase();
      return (p[0][0] || '?').toUpperCase();
    }
    return ((initials || '?').slice(0, 1)).toUpperCase();
  })();
  const bg = T.surfaceHi || T.surface;
  const tile = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(27,27,30,0.10)';
  const fs = monogram.length > 1 ? 27 : 34;
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none" style={{ flexShrink: 0, display: 'block' }} aria-hidden="true">
      <rect x="2" y="2" width="76" height="76" rx="23" fill={bg} stroke={T.line} strokeWidth="2" />
      {/* 2×2-MemberCard-Motiv (faint), bottom-right-Kachel koralle */}
      <g transform="translate(40,40) scale(0.34) translate(-80,-80)">
        <rect x="18" y="18" width="58" height="58" rx="17" fill={tile} />
        <rect x="84" y="18" width="58" height="58" rx="17" fill={tile} />
        <rect x="18" y="84" width="58" height="58" rx="17" fill={tile} />
        <rect x="84" y="84" width="58" height="58" rx="17" fill="#E4544A" />
      </g>
      <text x="40" y={monogram.length > 1 ? 48 : 50} textAnchor="middle" fontFamily={F.display} fontSize={fs} fontWeight="800" fill={T.text}>{monogram}</text>
    </svg>
  );
}

function StatTile({ label, value }) {
  const { T } = useTheme();
  return (
    <div style={{
      flex: 1,
      padding: '12px 10px',
      borderRadius: mr(16),
      background: T.surface,
      border: `1px solid ${T.line}`,
      textAlign: 'center',
      color: T.text,
    }}>
      <div style={{ fontFamily: F.display, fontSize: 20, fontWeight: 600, letterSpacing: '-0.03em' }}>{value}</div>
      <div style={{ fontSize: 10.5, color: T.sub, marginTop: 2 }}>{label}</div>
    </div>
  );
}

const INDICATOR_FONT = "'Manrope', 'Plus Jakarta Sans', system-ui, sans-serif";

function ChevronIndicator({ T }) {
  return (
    <svg className="mo-chev" width="9" height="15" viewBox="0 0 9 15" fill="none" style={{ display: 'block', flexShrink: 0 }}>
      <path d="M1.5 1.5L7 7.5L1.5 13.5" stroke={T.sub} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SettingRow({ icon, label, value, onClick, last, right }) {
  const { T } = useTheme();
  const [hover, setHover] = useState(false);
  return (
    <div
      className="mo-row"
      onClick={onClick}
      onMouseEnter={() => onClick && setHover(true)}
      onMouseLeave={() => onClick && setHover(false)}
      style={{
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        borderBottom: last ? 'none' : `1px solid ${T.line}`,
        cursor: onClick ? 'pointer' : 'default',
        background: hover && onClick ? T.itemHover : 'transparent',
        color: T.text,
        transition: `background ${DUR.fast}ms, color ${DUR.fast}ms`,
      }}
    >
      {icon && <span className="mo-lead" style={{ display: 'inline-flex', flexShrink: 0, color: T.red }}><Icon d={icon} size={18} stroke={T.sub} sw={2.4} /></span>}
      <span style={{ flex: 1, fontFamily: F.family, fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.01em' }}>{label}</span>
      {value !== undefined && value !== null && (
        <span
          style={{
            fontFamily: INDICATOR_FONT,
            fontSize: 13,
            fontWeight: 800,
            color: T.text,
            opacity: 0.9,
            letterSpacing: '-0.01em',
            fontFeatureSettings: '"tnum" 1, "cv11" 1',
            marginRight: right ? 0 : 6,
          }}
        >
          {value}
        </span>
      )}
      {right || (onClick && <ChevronIndicator T={T} />)}
    </div>
  );
}

function ConnectionsSheet({ open, connections, loaded, onClose, onOpenApi }) {
  const { T } = useTheme();
  const { t } = useT();
  const [mounted, setMounted] = useState(open);
  React.useEffect(() => {
    if (open) { setMounted(true); return; }
    if (mounted) {
      const id = setTimeout(() => setMounted(false), 280);
      return () => clearTimeout(id);
    }
  }, [open, mounted]);
  if (!mounted) return null;

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return '—'; }
  };

  return (
    <>
      <div
        className="backdrop-anim"
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 300, backdropFilter: 'blur(8px)' }}
      />
      <div
        className={open ? 'sheet-anim' : ''}
        style={{
          position: 'fixed',
          left: 0, right: 0, margin: '0 auto',
          width: '100%', maxWidth: 460,
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 64px)',
          background: T.surface,
          border: `1px solid ${T.line}`,
          borderRadius: 22,
          padding: '14px 14px 20px',
          zIndex: 301,
          color: T.text,
          transition: `transform ${DUR.slow}ms ease`,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{ width: 38, height: 4, borderRadius: mr(16), background: T.line, margin: '2px auto 14px' }} />
        <div style={{ fontFamily: F.family, fontSize: 13, fontWeight: 600, color: T.sub, padding: '0 6px 10px', letterSpacing: '-0.01em' }}>
          {t('connected_exchanges')}
        </div>

        <div style={{ borderRadius: mr(16), background: T.surfaceHigh, border: `1px solid ${T.line}`, overflow: 'hidden' }}>
          {!loaded ? (
            <div style={{ padding: '18px 16px', fontSize: 13, color: T.sub, fontFamily: F.family, textAlign: 'center' }}>
              {t('loading')}
            </div>
          ) : connections.length === 0 ? (
            <div style={{ padding: '18px 16px', fontSize: 13, color: T.sub, fontFamily: F.family, textAlign: 'center' }}>
              {t('no_api_connected')}
            </div>
          ) : connections.map((c, i) => {
            const label = API_EXCHANGES.find(e => e.id === c.exchangeId)?.label || c.label || c.exchangeId;
            return (
              <div
                className="mo-row"
                key={c.id}
                onClick={onOpenApi}
                style={{
                  padding: '12px 14px',
                  display: 'flex', alignItems: 'center', gap: 12,
                  borderBottom: i === connections.length - 1 ? 'none' : `1px solid ${T.line}`,
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <span className="mo-lead" style={{ display: 'inline-flex', flexShrink: 0 }}><ExchangeLogo name={label} size={32} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: F.family, fontSize: 14, fontWeight: 600, color: T.text, letterSpacing: '-0.01em' }}>
                    {c.label || label}
                  </div>
                  <div style={{ fontFamily: F.family, fontSize: 11.5, color: T.sub, marginTop: 2 }}>
                    {t('connected_since', { date: fmt(c.createdAt) })}
                  </div>
                </div>
                <svg className="mo-chev" width="7" height="12" viewBox="0 0 7 12" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M1 1L6 6L1 11" stroke={T.mute} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            );
          })}
        </div>

        <button
          onClick={onOpenApi}
          style={{
            width: '100%', padding: '12px', marginTop: 12,
            background: T.surfaceHigh, color: T.text,
            border: `1px solid ${T.line}`, borderRadius: mr(16),
            fontFamily: F.family, fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
          }}
        >{t('new_exchange_connect')}</button>
      </div>
    </>
  );
}

export default function ProfileScreen({ onResetData, txCount = 0, txs = [], user, onOpenPrivacy, onOpenPricing, onOpenApi, onOpenWallets, onOpenAlerts, onOpenSupport }) {
  const { T, isDark, setIsDark } = useTheme();
  const { isWide } = useBreakpoint();
  const { currency, setCurrency } = useCurrency();
  const { plan, setPlan, unlockWithCode, createVipCode, isOwner } = useUserPlan();
  const { name, setName, initials, language, setLanguage } = useProfile();
  const { privateMode, setPrivateMode } = usePrivacy();
  const { signOut, linkWithApple } = useAuth();
  const [linkingApple, setLinkingApple] = useState(false);
  const [linkAppleError, setLinkAppleError] = useState('');
  const appleLinked = user?.providerData?.some(p => p.providerId === 'apple.com');
  const { t } = useT();

  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(name);
  const [vipModal, setVipModal] = useState(false);
  const [vipCode, setVipCode] = useState('');
  const [vipShake, setVipShake] = useState(false);
  const [vipError, setVipError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdCode, setCreatedCode] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmDeleteAccount, setConfirmDeleteAccount] = useState(false);
  const [accountDeleting, setAccountDeleting] = useState(false);
  const [accountDeleteError, setAccountDeleteError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [langSheet, setLangSheet] = useState(false);
  const [curSheet, setCurSheet] = useState(false);
  const [connections, setConnections] = useState([]);
  const [connectionsLoaded, setConnectionsLoaded] = useState(false);
  const [connSheet, setConnSheet] = useState(false);
  const [memberNo, setMemberNo] = useState('——');

  useEffect(() => {
    if (!user?.uid) return;
    getOrAssignMemberNumber(user.uid).then(n => { if (n) setMemberNo(formatMemberNo(n)); }).catch(() => {});
  }, [user?.uid]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await listConnections();
        if (!cancelled) {
          setConnections(list || []);
          setConnectionsLoaded(true);
        }
      } catch {
        if (!cancelled) setConnectionsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const reloadConnections = async () => {
    try {
      const list = await listConnections();
      setConnections(list || []);
    } catch { /* ignore */ }
  };

  const handleExportData = async () => {
    if (!user) return;
    setExporting(true);
    try {
      const data = await exportUserData(user);
      downloadUserDataAsJson(data, `red-export-${user.uid.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.json`);
    } catch (e) {
      setAccountDeleteError(t('prof_export_failed', { msg: e?.message || t('prof_unknown_error') }));
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    setAccountDeleting(true);
    setAccountDeleteError('');
    try {
      await deleteAccountCompletely(user);
      // deleteUser meldet automatisch ab — AuthContext reagiert
    } catch (e) {
      const code = e?.code || '';
      if (code === 'auth/requires-recent-login') {
        setAccountDeleteError(t('prof_reauth_required'));
      } else {
        setAccountDeleteError(t('prof_account_delete_failed', { msg: e?.message || t('prof_unknown_error') }));
      }
      setAccountDeleting(false);
    }
  };

  const planColors = PLAN_COLORS(T);
  const planColor = planColors[plan] || T.mute;

  const exchangeCount = connections.length;

  const positionCount = useMemo(() => {
    const set = new Set();
    txs.forEach(tx => {
      if (tx.coin) set.add(tx.coin);
      if (tx.coinIn) set.add(tx.coinIn);
      if (tx.coinOut) set.add(tx.coinOut);
    });
    return set.size;
  }, [txs]);

  // Börsen & Wallets aus den TX (gleiche Quelle wie Datencheck — alle Herkünfte,
  // nicht nur live-verbundene API-Börsen).
  const sources = useMemo(() => buildDataCheck({ txs, marketData: {} }).sources, [txs]);

  const saveName = () => {
    setName(nameInput.trim());
    setEditingName(false);
  };

  const handleVip = async () => {
    const res = await unlockWithCode(vipCode);
    if (res?.ok) {
      setVipError('');
      setVipModal(false);
      setVipCode('');
    } else {
      const msg = res?.reason === 'used' ? t('prof_vip_err_used')
        : res?.reason === 'invalid' ? t('prof_vip_err_invalid')
        : res?.reason === 'empty' ? t('prof_vip_err_empty')
        : t('prof_vip_err_generic');
      setVipError(msg);
      setVipShake(true); setTimeout(() => setVipShake(false), 500);
    }
  };

  // Owner: neuen Geschenk-Code erzeugen
  const handleCreateCode = async () => {
    setCreating(true);
    setCreatedCode('');
    setVipError('');
    try {
      const res = await createVipCode?.({});
      if (res?.ok) setCreatedCode(res.code);
      else setVipError(t('prof_code_create_failed'));
    } finally {
      setCreating(false);
    }
  };

  // Beim Schließen des Dialogs Eingaben/Meldungen zurücksetzen
  useEffect(() => {
    if (!vipModal) { setVipError(''); setCreatedCode(''); }
  }, [vipModal]);

  // ── DESKTOP: Handoff red-screens-more.jsx Profile() ──────────────────────
  if (isWide) {
    return (
      <DesktopProfile
        T={T} t={t}
        isDark={isDark} setIsDark={setIsDark}
        currency={currency} setCurrency={setCurrency}
        language={language} setLanguage={setLanguage}
        plan={plan}
        name={name} initials={initials}
        editingName={editingName} setEditingName={setEditingName}
        nameInput={nameInput} setNameInput={setNameInput} saveName={saveName}
        user={user}
        connections={connections} reloadConnections={reloadConnections} setConnSheet={setConnSheet}
        sources={sources}
        txCount={txCount}
        positionCount={positionCount}
        memberNo={memberNo}
        isOwner={isOwner}
        signOut={signOut}
        exporting={exporting} handleExportData={handleExportData}
        setConfirmReset={setConfirmReset}
        setConfirmDeleteAccount={setConfirmDeleteAccount}
        onOpenPrivacy={onOpenPrivacy}
        onOpenPricing={onOpenPricing}
        onOpenApi={onOpenApi}
        onOpenWallets={onOpenWallets}
        onOpenAlerts={onOpenAlerts}
        onOpenSupport={onOpenSupport}
        setVipModal={setVipModal}
        modals={
          <DesktopProfileModals
            vipModal={vipModal} setVipModal={setVipModal}
            vipCode={vipCode} setVipCode={setVipCode}
            vipShake={vipShake} handleVip={handleVip}
            vipError={vipError} isOwner={isOwner}
            creating={creating} createdCode={createdCode} handleCreateCode={handleCreateCode}
            confirmReset={confirmReset} setConfirmReset={setConfirmReset}
            onResetData={onResetData}
            confirmDeleteAccount={confirmDeleteAccount}
            setConfirmDeleteAccount={setConfirmDeleteAccount}
            accountDeleting={accountDeleting} accountDeleteError={accountDeleteError}
            handleDeleteAccount={handleDeleteAccount}
            T={T} t={t}
          />
        }
      />
    );
  }

  // memberSince — kurzes Format für mobile Anzeige (z.B. "DEZ. 2024")
  const memberSinceMobile = (() => {
    try {
      const _ts = user?.metadata?.creationTime;
      if (!_ts) return '——';
      const d = new Date(_ts);
      const mo = d.toLocaleDateString('de-DE', { month: 'short' }).toUpperCase().replace('.', '');
      return `${mo}. ${d.getFullYear()}`;
    } catch { return '——'; }
  })();

  const initial = (name?.trim()?.[0] || initials?.[0] || '?').toUpperCase();

  // Inline-Helpers für V5 Mobile
  const Divider = () => <div style={{ height: 1, background: T.lineSoft || T.line }} />;
  // Ruhige Sentence-Case-Sektionsüberschrift (wie Desktop) — kein Koral-Punkt,
  // kein Caps/Tracking mehr.
  const SecHead = ({ label }) => (
    <div style={{ padding: '0 22px 10px' }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: T.sub, letterSpacing: '-0.01em', fontFamily: F.family }}>{label}</span>
    </div>
  );

  return (
    <div style={{ padding: '8px 0 100px', color: T.text, position: 'relative' }}>

      {/* ── IDENTITY ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '28px 22px 24px' }}>
        <Avatar T={T} isDark={isDark} name={name} initials={initials} />
        <div style={{ flex: 1, minWidth: 0 }}>
          {editingName ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveName()}
                autoFocus
                style={{ flex: 1, padding: '6px 10px', borderRadius: mr(12), background: T.surfaceHigh, border: `1px solid ${T.line}`, color: T.text, fontSize: 14, fontFamily: F.family }}
              />
              <button onClick={saveName} style={{ padding: '6px 10px', borderRadius: mr(12), background: T.red, color: '#fff', border: 'none', cursor: 'pointer', fontFamily: F.family, fontSize: 12, fontWeight: 600 }}>OK</button>
            </div>
          ) : (
            <div onClick={() => { setNameInput(name); setEditingName(true); }} style={{ cursor: 'pointer' }}>
              <div style={{ fontFamily: F.display, fontSize: 24, fontWeight: 900, letterSpacing: '-0.05em', lineHeight: 1.05, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {name || t('enter_name')}
              </div>
            </div>
          )}
          {!editingName && <TierBadge tier={plan === 'free' ? null : plan} />}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {/* Glocke (aus dem Hero hierher gewandert) — neben dem Namens-Stift.
              Im Namens-Edit-Modus ausgeblendet, damit der OK-Knopf des Namens nicht
              direkt neben der Glocke klebt; kommt nach Bestätigen (OK) zurück. */}
          {!editingName && <NotificationBell variant="circle" onManage={onOpenAlerts} />}
          <button
            onClick={() => { setNameInput(name); setEditingName(true); }}
            style={{ width: 36, height: 36, borderRadius: '50%', background: T.surface, border: `1px solid ${T.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
            aria-label={t('prof_edit_name')}
          >
            <Icon d={Icons.edit} size={16} stroke={T.sub} sw={1.8} />
          </button>
        </div>
      </div>

      <Divider />

      {/* ── STATS ── gleichmäßige Zellen, zentriert, Hairline-Teiler dazwischen.
         Label nowrap+ellipsis, damit „Transaktionen" die Spalten nicht quetscht. */}
      <div style={{ display: 'flex', padding: '24px 18px' }}>
        {[
          { val: positionCount, lbl: t('positions') },
          { val: txCount, lbl: t('transactions') },
          { val: sources.length || exchangeCount, lbl: t('sources') },
        ].map(({ val, lbl }, i) => (
          <div key={lbl} style={{ flex: 1, minWidth: 0, textAlign: 'center', padding: '0 6px', borderLeft: i > 0 ? `1px solid ${T.lineSoft || T.line}` : 'none' }}>
            <div style={{ fontFamily: F.num, fontSize: 30, fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 1 }}>{val}</div>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: T.mute, letterSpacing: '0.04em', textTransform: 'uppercase', marginTop: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lbl}</div>
          </div>
        ))}
      </div>

      <Divider />

      {/* ── MITGLIED NR. — typografischer Hauptmoment ── */}
      <div style={{ padding: '32px 22px 28px', position: 'relative', overflow: 'hidden' }}>
        {/* MCGridMark Wasserzeichen hinter der Zahl */}
        <MCGridMark
          size={200}
          cells={withAlpha(T.text, 0.055)}
          dot={withAlpha(T.text, 0.055)}
          plus={T.red}
          style={{ position: 'absolute', right: -24, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
        />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.mute, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 8 }}>
            {t('member_since') || 'Mitglied seit'} {memberSinceMobile}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.mute, letterSpacing: '0.04em', position: 'relative', top: -6 }}>Nº</span>
            <span style={{ fontFamily: F.num, fontSize: 80, fontWeight: 900, letterSpacing: '-0.06em', lineHeight: 0.9 }}>{memberNo}</span>
          </div>
        </div>
      </div>

      <Divider />

      {/* ── QUICK ACTION DOCK ── */}
      <div style={{ display: 'flex', padding: '24px 22px' }}>
        {[
          { icon: Icons.link,         label: t('sources') || 'Quellen',    action: () => onOpenApi?.() },
          { icon: Icons.walletCard,   label: t('wallets') || 'Wallets',    action: () => onOpenWallets?.() },
          { icon: Icons.bell,         label: t('alerts') || 'Alarme',      action: () => onOpenAlerts?.() },
          { icon: Icons.privacyShield,label: t('privacy') || 'Datenschutz',action: () => onOpenPrivacy?.() },
        ].map(({ icon, label, action }) => (
          <button
            key={label}
            onClick={action}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0, WebkitTapHighlightColor: 'transparent', color: T.red }}
          >
            <Icon d={icon} size={22} stroke={T.sub} sw={1.7} />
            <span style={{ fontSize: 10.5, fontWeight: 600, color: T.mute, letterSpacing: '-0.01em', fontFamily: F.family }}>{label}</span>
          </button>
        ))}
      </div>

      <Divider />

      {/* ── EINSTELLUNGEN ── */}
      <div style={{ paddingTop: 24 }}>
        <SecHead label={t('appearance') || 'Einstellungen'} />
        <SettingRow icon={Icons.themeMoon}    label={t('dark_mode')}    right={<Toggle on={isDark} onChange={setIsDark} />}           last={false} />
        <SettingRow icon={Icons.eyeOff}       label={t('private_mode')} right={<Toggle on={privateMode} onChange={setPrivateMode} />} last={false} />
        <SettingRow icon={Icons.language}     label={t('language')}     value={(language || 'de').toUpperCase()} onClick={() => setLangSheet(true)} last={false} />
        <SettingRow icon={Icons.currency}     label={t('currency')}     value={currency === 'USD' ? 'USD $' : 'EUR €'} onClick={() => setCurSheet(true)} last={false} />
        <SettingRow icon={Icons.privacyShield}label={t('privacy')}      onClick={() => onOpenPrivacy?.()}  last={false} />
        <SettingRow icon={Icons.headset}      label={t('support')}      onClick={() => onOpenSupport?.()}  last={!isOwner && appleLinked} />
        {isOwner && (
          <SettingRow icon={Icons.starFilled} label={t('prof_create_vip_code')} onClick={() => setVipModal(true)} last={appleLinked} />
        )}
        {!appleLinked && (
          <SettingRow
            icon={Icons.apple}
            label={linkingApple ? '…' : (language === 'en' ? 'Connect Apple' : 'Apple verbinden')}
            value={linkAppleError || undefined}
            onClick={linkingApple ? undefined : async () => {
              setLinkingApple(true); setLinkAppleError('');
              try { await linkWithApple(); } catch (e) {
                console.error('[linkWithApple]', e);
                const msg = e?.code === 'auth/credential-already-in-use'
                  ? (language === 'en' ? 'Already linked to another account' : 'Bereits mit anderem Account verknüpft')
                  : (e?.code || e?.message || String(e) || 'Fehler');
                setLinkAppleError(msg.slice(0, 80));
                setLinkingApple(false);
              }
            }}
            last
          />
        )}
      </div>

      {/* ── DATEN ── */}
      <div style={{ paddingTop: 28 }}>
        <SecHead label={t('prof_your_data_gdpr_paren') || 'Daten'} />
        <SettingRow icon={Icons.export} label={exporting ? t('exporting') : t('export_data_json')} onClick={exporting ? undefined : handleExportData} last={false} />
        <SettingRow icon={Icons.trashSettings} label={t('delete_data')} onClick={() => setConfirmReset(true)} last />
      </div>

      {/* PromoCard */}
      {plan !== 'vip' && (
        <div style={{ margin: '28px 16px 0' }}>
          <PromoCard
            targetPlan={plan === 'free' ? 'premium' : 'vip'}
            onClick={() => { if (onOpenPricing) onOpenPricing(); else if (plan === 'free') setPlan('premium'); else setVipModal(true); }}
          />
        </div>
      )}

      {/* ── iOS-STYLE SESSION BUTTONS ── */}
      <div style={{ margin: '28px 16px 0', border: `0.5px solid ${T.line}`, borderRadius: 14, overflow: 'hidden', background: T.surface }}>
        <button
          onClick={async () => { try { await signOut(); } catch {} }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: 17, background: 'none', border: 'none', cursor: 'pointer', fontFamily: F.family, fontSize: 16, fontWeight: 400, color: T.red, WebkitTapHighlightColor: 'transparent' }}
        >
          {t('sign_out')}
        </button>
      </div>
      <div style={{ margin: '12px 16px 0', border: `0.5px solid ${T.line}`, borderRadius: 14, overflow: 'hidden', background: T.surface }}>
        <button
          onClick={() => setConfirmDeleteAccount(true)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: 14, background: 'none', border: 'none', cursor: 'pointer', fontFamily: F.family, fontSize: 14, fontWeight: 400, color: withAlpha(T.red, 0.45), WebkitTapHighlightColor: 'transparent' }}
        >
          {t('delete_account_full')}
        </button>
      </div>

      {/* Brand footer */}
      <div style={{ textAlign: 'center', padding: '32px 0 8px' }}>
        <div style={{ display: 'inline-flex', justifyContent: 'center' }}>
          <RedWordmark size={24} />
        </div>

        {/* Socials in Koralle */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 32, marginTop: 20 }}>
          {[
            { icon: Icons.socialX,        href: 'https://x.com/redcrypto_de',                                    label: 'X',         filled: true  },
            { icon: Icons.socialInstagram, href: 'https://www.instagram.com/red_crypto_/',                       label: 'Instagram', filled: false },
            { icon: Icons.socialLinkedin,  href: 'https://www.linkedin.com/in/christian-rothlehner-7000863a0/', label: 'LinkedIn',  filled: false },
          ].map(({ icon, href, label, filled }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              style={{ display: 'inline-flex', WebkitTapHighlightColor: 'transparent', color: T.red }}
            >
              <Icon d={icon} size={20} stroke={filled ? 'none' : T.red} sw={1.7} fill={filled ? T.red : 'none'} />
            </a>
          ))}
        </div>

        <div style={{ marginTop: 16, fontSize: 11, color: T.mute, lineHeight: 1.7 }}>
          <a href="https://redcrypto.de/impressum" target="_blank" rel="noopener noreferrer" style={{ color: T.mute, textDecoration: 'underline' }}>Impressum</a>
          {' · '}
          <a href="https://redcrypto.de/agb" target="_blank" rel="noopener noreferrer" style={{ color: T.mute, textDecoration: 'underline' }}>AGB</a>
          {' · '}
          <a href="https://redcrypto.de/datenschutz" target="_blank" rel="noopener noreferrer" style={{ color: T.mute, textDecoration: 'underline' }}>Datenschutz</a>
        </div>
      </div>

      {/* VIP modal */}
      {vipModal && (
        <div
          onClick={() => setVipModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 320,
              padding: 22, borderRadius: mr(16),
              background: T.surface, border: `1px solid ${T.silverBorder}`,
              boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
              animation: vipShake ? 'vipShake 0.5s' : 'none',
            }}
          >
            <div style={{ fontFamily: F.display, fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em', marginBottom: 8 }}>{t('redeem_vip_code')}</div>
            <div style={{ fontSize: 12, color: T.sub, marginBottom: 14 }}>{t('prof_vip_code_desc')}</div>
            <input
              value={vipCode}
              onChange={e => setVipCode(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && handleVip()}
              placeholder="RED-VIP-…"
              style={{ width: '100%', padding: '12px 14px', borderRadius: mr(16), background: T.surfaceHigh, border: `1px solid ${T.line}`, color: T.text, fontFamily: F.mono, fontSize: 14, letterSpacing: '0.06em', textAlign: 'center', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <RedButton variant="dark" size="md" fullWidth onClick={() => setVipModal(false)}>{t('cancel')}</RedButton>
              <RedButton variant="silver" size="md" fullWidth onClick={handleVip}>{t('redeem')}</RedButton>
            </div>
            {vipError && (
              <div style={{ marginTop: 12, fontSize: 12, color: RED_PRO, textAlign: 'center' }}>{vipError}</div>
            )}
            {isOwner && (
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.line}` }}>
                <div style={{ fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.mute, marginBottom: 8 }}>{t('prof_owner_gift_code')}</div>
                {createdCode ? (
                  <>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div style={{ flex: 1, fontFamily: F.mono, fontSize: 14, letterSpacing: '0.06em', color: T.text, textAlign: 'center', padding: '10px 8px', background: T.surfaceHigh || T.surface, borderRadius: mr(16), border: `1px solid ${T.line}` }}>{createdCode}</div>
                      <RedButton variant="dark" size="md" onClick={() => navigator.clipboard?.writeText(createdCode)}>{t('copy')}</RedButton>
                    </div>
                    <div style={{ marginTop: 8, fontSize: 11, color: T.mute, textAlign: 'center' }}>{t('prof_vip_once_redeemable')}</div>
                  </>
                ) : (
                  <RedButton variant="dark" size="md" fullWidth onClick={handleCreateCode}>{creating ? t('creating') : t('prof_create_vip_code')}</RedButton>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {confirmReset && (
        <div
          onClick={() => setConfirmReset(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 320, padding: 22, borderRadius: mr(16), background: T.surface, border: `1px solid ${T.line}`, color: T.text }}>
            <div style={{ fontFamily: F.display, fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em', marginBottom: 8 }}>{t('delete_all_data_q')}</div>
            <div style={{ fontSize: 12, color: T.sub, marginBottom: 14 }}>{t('delete_confirm')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <RedButton variant="dark" size="md" fullWidth onClick={() => setConfirmReset(false)}>{t('cancel')}</RedButton>
              <RedButton variant="primary" size="md" fullWidth onClick={async () => { setConfirmReset(false); try { await onResetData?.(); } catch {} }}>{t('delete')}</RedButton>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteAccount && (
        <div
          onClick={() => !accountDeleting && setConfirmDeleteAccount(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 360, padding: 22, borderRadius: mr(16), background: T.surface, border: `1px solid ${withAlpha(T.red, 0.4)}`, color: T.text }}>
            <div style={{ fontFamily: F.display, fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em', marginBottom: 8, color: T.red }}>
              {t('delete_account_q')}
            </div>
            <div style={{ fontSize: 12, color: T.sub, marginBottom: 14, lineHeight: 1.6 }}>
              {t('prof_delete_account_intro_pre')}<strong>{t('irreversible')}</strong>{t('prof_delete_account_intro_post')}
              <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
                <li>{t('prof_delete_item_txs')}</li>
                <li>{t('prof_delete_item_imports')}</li>
                <li>{t('prof_delete_item_login')}</li>
              </ul>
              <div style={{ marginTop: 10, fontSize: 11, color: T.muted }}>
                {t('prof_delete_export_tip')}
              </div>
            </div>
            {accountDeleteError && (
              <div style={{ fontSize: 12, color: T.red, marginBottom: 12, lineHeight: 1.5 }}>
                {accountDeleteError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <RedButton
                variant="dark" size="md" fullWidth
                onClick={() => !accountDeleting && setConfirmDeleteAccount(false)}
              >
                {t('cancel')}
              </RedButton>
              <RedButton
                variant="primary" size="md" fullWidth
                onClick={handleDeleteAccount}
                disabled={accountDeleting}
              >
                {accountDeleting ? t('prof_deleting') : t('prof_delete_final_btn')}
              </RedButton>
            </div>
          </div>
        </div>
      )}

      <SelectSheet
        open={langSheet}
        title={t('language')}
        value={language || 'de'}
        onSelect={setLanguage}
        onClose={() => setLangSheet(false)}
        options={[
          { value: 'de', label: 'Deutsch (DE)' },
          { value: 'en', label: 'English (EN)' },
        ]}
      />
      <SelectSheet
        open={curSheet}
        title={t('currency')}
        value={currency}
        onSelect={setCurrency}
        onClose={() => setCurSheet(false)}
        options={[
          { value: 'EUR', label: 'EUR €' },
          { value: 'USD', label: 'USD $' },
        ]}
      />

      <ConnectionsSheet
        open={connSheet}
        connections={connections}
        loaded={connectionsLoaded}
        onClose={() => setConnSheet(false)}
        onOpenApi={() => { setConnSheet(false); onOpenApi?.(); }}
      />

      <style>{`
        @keyframes vipShake {
          0%,100% { transform: translateX(0); }
          25%     { transform: translateX(-6px); }
          75%     { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}
